import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BracketView, type BracketMatch } from "../components/BracketView";
import { GameCard } from "../components/GameCard";
import { Pagination } from "../components/Pagination";
import { SectionCard } from "../components/SectionCard";
import { TournamentStatusPill } from "../components/TournamentStatusPill";
import { useAuth } from "../context/AuthContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api } from "../lib/api";
import type { TeamStatus, TournamentDetail } from "../types/domain";

const STANDINGS_PAGE_SIZE = 5;
const GAMES_PAGE_SIZE = 5;

function standingLabel(status: TeamStatus, qualifies: boolean): string {
  if (status === "dissolved") return "Dissous";
  return qualifies ? "Qualifiée" : "Provisoire";
}

function buildBracketPreview(tournament: TournamentDetail): BracketMatch[] {
  const qualified = tournament.standingRows.filter(
    (r) => r.status === "active" && r.qualifiesForFinals,
  );

  const team = (rank: number) => qualified[rank - 1]?.teamName ?? null;

  return [
    {
      id: "sf1",
      round: "semi_final",
      position: 1,
      leftTeam: team(1),
      rightTeam: team(4),
      leftScore: null,
      rightScore: null,
      winner: null,
      gameId: null,
      status: team(1) && team(4) ? "ready" : "pending",
    },
    {
      id: "sf2",
      round: "semi_final",
      position: 2,
      leftTeam: team(2),
      rightTeam: team(3),
      leftScore: null,
      rightScore: null,
      winner: null,
      gameId: null,
      status: team(2) && team(3) ? "ready" : "pending",
    },
    {
      id: "final",
      round: "final",
      position: 1,
      leftTeam: null,
      rightTeam: null,
      leftScore: null,
      rightScore: null,
      winner: null,
      gameId: null,
      status: "pending",
    },
    {
      id: "petit_final",
      round: "petit_final",
      position: 1,
      leftTeam: null,
      rightTeam: null,
      leftScore: null,
      rightScore: null,
      winner: null,
      gameId: null,
      status: "pending",
    },
  ];
}

export function TournamentDetailPage() {
  const { tournamentId = "" } = useParams();
  const { user } = useAuth();
  const { setTrail } = useBreadcrumbs();
  const [tournament, setTournament] = useState<TournamentDetail | null>(null);
  const [bracketMatches, setBracketMatches] = useState<BracketMatch[]>([]);
  const [standingsPage, setStandingsPage] = useState(1);
  const [gamesPage, setGamesPage] = useState(1);

  useEffect(() => {
    let active = true;
    setTrail([]);
    void api.getTournament(tournamentId).then((t) => {
      if (!active) return;
      setTournament(t);
      setTrail([{ label: "Tournois", to: "/tournaments" }, { label: t.name }]);
    });
    void api.listFinals(tournamentId).then((matches) => {
      if (!active) return;
      if (matches.length > 0) {
        setBracketMatches(matches);
      }
    });
    return () => {
      active = false;
    };
  }, [tournamentId, setTrail]);

  if (!tournament) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-ink">{tournament.name}</h1>
          <TournamentStatusPill status={tournament.status} />
        </div>
        {user?.role === "admin" && (
          <Link
            to={`/admin/tournaments/${tournament.id}`}
            className="mt-1 text-sm text-smoke hover:text-ink"
          >
            Administration
          </Link>
        )}
        {tournament.description && (
          <p className="mt-1 text-sm text-smoke whitespace-pre-line">
            {tournament.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-smoke">
            {tournament.teamCount} équipes
          </span>
          {tournament.ruleHighlights.map((rule) => (
            <span
              key={rule}
              className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-smoke"
            >
              {rule}
            </span>
          ))}
        </div>

        {(tournament.status === "registration_open" ||
          tournament.status === "registration_closed") &&
          !tournament.myTeamId && (
            <div className="mt-4">
              <Link
                to={`/tournaments/${tournament.id}/team`}
                className="btn-secondary"
              >
                Créer une équipe
              </Link>
            </div>
          )}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {bracketMatches.length > 0 && (
          <SectionCard title="Phase finale">
            <BracketView matches={bracketMatches} />
          </SectionCard>
        )}

        <SectionCard title="Classement">
          {tournament.standingRows.length > 0 ? (
            <div className="space-y-2">
              {/* Header — hidden on mobile, full 5-col grid on sm+ */}
              <div className="hidden sm:grid sm:grid-cols-[40px,1fr,72px,80px,120px] gap-2 rounded-xl bg-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-smoke">
                <span>#</span>
                <span>Équipe</span>
                <span className="text-right">Points</span>
                <span className="text-right">Levées</span>
                <span className="text-right">Points/Levées</span>
              </div>
              {tournament.standingRows
                .slice(
                  (standingsPage - 1) * STANDINGS_PAGE_SIZE,
                  standingsPage * STANDINGS_PAGE_SIZE,
                )
                .map((row) => {
                  const isDissolved = row.status === "dissolved";
                  const ptsPerLevee =
                    row.countedHands > 0
                      ? (row.points / row.countedHands).toFixed(1)
                      : "-";
                  const leveeClass =
                    row.countedHands >= row.quota
                      ? "font-bold text-emerald-600"
                      : "text-smoke";
                  return (
                    <div
                      key={row.teamId}
                      className={`card flex items-center gap-3 py-3 sm:grid sm:grid-cols-[40px,1fr,72px,80px,120px] sm:gap-2 sm:items-center ${isDissolved ? "opacity-50" : ""}`}
                    >
                      {/* Rank */}
                      <span className="w-8 shrink-0 text-lg font-bold text-ember sm:w-auto">
                        {isDissolved ? "-" : row.rank}
                      </span>

                      {/* Team info */}
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-ink">
                          {row.teamName}
                        </span>
                        {(row.playerOnePseudo || row.playerTwoPseudo) && (
                          <span className="block truncate text-xs text-smoke">
                            {row.playerOnePseudo}
                            {row.playerOnePseudo && row.playerTwoPseudo
                              ? ", "
                              : ""}
                            {row.playerTwoPseudo}
                          </span>
                        )}
                        {(tournament.status === "in_progress" ||
                          tournament.status === "finished") && (
                          <span
                            className={`status-pill block mt-1 ${
                              !isDissolved && row.qualifiesForFinals
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {standingLabel(row.status, row.qualifiesForFinals)}
                          </span>
                        )}
                      </span>

                      {/* Stats: stacked on mobile, dissolved into grid cells on sm+ */}
                      <div className="flex shrink-0 flex-col items-end gap-0.5 sm:contents">
                        <span className="whitespace-nowrap text-sm font-semibold text-ink sm:text-right sm:font-normal">
                          <span className="mr-1 text-xs font-normal text-smoke sm:hidden">
                            Points
                          </span>
                          {row.points}
                        </span>
                        <span
                          className={`whitespace-nowrap text-xs sm:text-sm sm:text-right ${leveeClass}`}
                        >
                          <span className="mr-1 font-normal text-smoke sm:hidden">
                            Levées
                          </span>
                          {row.countedHands}/{row.quota}
                        </span>
                        <span className="whitespace-nowrap text-xs text-smoke sm:text-sm sm:text-right sm:text-ink">
                          <span className="mr-1 text-smoke sm:hidden">
                            Points/Levées
                          </span>
                          {ptsPerLevee}
                        </span>
                      </div>
                    </div>
                  );
                })}
              {tournament.standingRows.length > STANDINGS_PAGE_SIZE && (
                <Pagination
                  page={standingsPage}
                  totalPages={Math.ceil(
                    tournament.standingRows.length / STANDINGS_PAGE_SIZE,
                  )}
                  onPageChange={setStandingsPage}
                />
              )}
            </div>
          ) : (
            <p className="text-sm text-smoke">Aucun classement disponible.</p>
          )}
        </SectionCard>

        <SectionCard
          title="Parties"
          action={
            tournament.status === "in_progress" &&
            tournament.myTeamId && (
              <Link to="/games/new" className="btn-primary text-sm">
                + Nouvelle partie
              </Link>
            )
          }
        >
          {tournament.recentGames.length > 0 ? (
            <div className="space-y-3">
              {tournament.recentGames
                .slice(
                  (gamesPage - 1) * GAMES_PAGE_SIZE,
                  gamesPage * GAMES_PAGE_SIZE,
                )
                .map((game) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    tournamentName={tournament.name}
                    showTournamentName={false}
                  />
                ))}
              {tournament.recentGames.length > GAMES_PAGE_SIZE && (
                <Pagination
                  page={gamesPage}
                  totalPages={Math.ceil(
                    tournament.recentGames.length / GAMES_PAGE_SIZE,
                  )}
                  onPageChange={setGamesPage}
                />
              )}
            </div>
          ) : (
            <p className="text-sm text-smoke">Aucune partie jouée.</p>
          )}
        </SectionCard>
      </div>

      {/* Preview bracket when no finals generated yet */}
      {bracketMatches.length === 0 && (
        <SectionCard title="Phase finale">
          <BracketView matches={buildBracketPreview(tournament)} />
        </SectionCard>
      )}
    </div>
  );
}
